// @flow

import clsx from 'clsx';

import type { ProductId, Process, ProductType } from '@gandi/cart';
import { useTranslator } from '@gandi/translate';
import {
  Base,
  IconButton,
  useDisclosure,
  UnorderedList,
  TypoListItem,
  Text,
} from '@gandi/ui';
import { ListRow, ListItem } from '@gandi/ui/deprecated';

import { useFeatureFlags } from 'src/hooks/useFeatureFlag';

import { DomainConfigurationCellContents } from './DomainConfiguration/CellContents';
import { DomainConfigurationRowContents } from './DomainConfiguration/RowContents';
import { DomainContactsCellContents } from './DomainContacts/CellContents';
import { OwnerCellContents } from './Owner/CellContents';
import { DomainOwnerRowContents } from './Owner/RowContents';

import styles from './ProductSetup.scss';

type Props = {
  firstProductId: ProductId,
  productType: ProductType,
  process: Process,
};

/** @legacy To remove after FF_CART_OVERVIEW_NDS */
function MicroCell({
  children,
  secondary = false,
}: {
  children: React.Node,
  secondary?: boolean,
}) {
  const { FF_CART_OVERVIEW_NDS } = useFeatureFlags();
  if (FF_CART_OVERVIEW_NDS) return children;

  // Legacy style
  return (
    <span className={clsx(styles.cell, { [styles.secondary]: secondary })}>
      {children}
    </span>
  );
}

function ProductSetup({
  firstProductId,
  productType,
  process,
}: Props): null | React.Node {
  const domain = productType === 'domain';
  const domainCreate = domain && process === 'create';
  const domainTransfer = domain && process === 'transfer';
  const domainChangeOwner = domain && process === 'change_owner';

  if (domainChangeOwner) {
    return null;
  }

  const collapsible = domainCreate;

  return (
    <ProductSetupContainer
      extra={
        collapsible ? (
          <UnorderedList gap="xxsmall" paddingLeft="none" listStyleType="none">
            <TypoListItem>
              <DomainOwnerRowContents productId={firstProductId} />
            </TypoListItem>
            <TypoListItem>
              <DomainConfigurationRowContents process="create" />
            </TypoListItem>
          </UnorderedList>
        ) : null
      }
    >
      {!domainTransfer && (
        <MicroCell secondary>
          <OwnerCellContents productId={firstProductId} />
        </MicroCell>
      )}
      {(domainCreate || domainTransfer) && (
        <MicroCell secondary>
          <DomainConfigurationCellContents process={process} />
        </MicroCell>
      )}
      {(domainCreate || domainTransfer) && (
        <MicroCell secondary>
          <DomainContactsCellContents productId={firstProductId} />
        </MicroCell>
      )}
    </ProductSetupContainer>
  );
}

function ProductSetupContainer({
  children,
  extra,
}: {
  children: React.Node,
  extra?: React.Node,
}): React.MixedElement {
  const { __ } = useTranslator();
  const { open, triggerProps, contentProps } = useDisclosure();
  const { FF_CART_OVERVIEW_NDS } = useFeatureFlags();

  if (FF_CART_OVERVIEW_NDS) {
    return (
      <Base className={styles.root} color="weak" flex flexCol gap="spacing-2x">
        <div className={styles.container}>
          <Text weight="semibold">{__('Setup details')}</Text>
          {children}
        </div>
        {extra}
      </Base>
    );
  }

  /** Legacy style */

  return (
    <div className={styles.rootLegacy}>
      <ListRow>
        <ListItem className={styles.row} backgroundColor="white">
          <MicroCell>{__('Setup details')}</MicroCell>
          {children}
        </ListItem>
        {!!extra && (
          <Base
            flex
            flexAlignItemsCenter
            paddingX="xsmall"
            backgroundColor="white"
          >
            <IconButton
              small
              kind="ghost"
              shape="circle"
              {...triggerProps}
              icon={open ? 'chevronTop' : 'chevronBottom'}
            >
              {__('Toggle')}
            </IconButton>
          </Base>
        )}
      </ListRow>

      {!!extra && (
        <Base
          padding="small"
          separatorTop
          color="light"
          backgroundColor="white"
          {...contentProps}
        >
          {extra}
        </Base>
      )}
    </div>
  );
}

export default ProductSetup;
